import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AlertTriangle, CheckCircle2, FileSearch, HelpCircle, ShieldAlert, ShieldCheck, Upload } from 'lucide-react'

import { useTenantBranding } from '../../contexts/TenantContext'

const API_BASE = import.meta.env.VITE_PROJECTS_API_URL || '/api/projects'

/**
 * Check a signed document. Public, no login, nothing to browse.
 *
 * WHO THIS IS FOR. The people who most need to check a signed document are the ones who will never
 * have an account here: the client's accounts department, their lawyer, a bank, an auditor. A
 * verification only we can run is worth nothing in the dispute it exists for.
 *
 * TWO ANSWERS, SHOWN SEPARATELY, AND THEY MUST STAY SEPARATE.
 *
 *   * The SEAL is read out of the file itself. It needs no records at all — if our register were
 *     wiped tomorrow this answer would not change.
 *   * The REGISTER is a fingerprint lookup. If the seal were forged with some other key, this
 *     answer would still be no.
 *
 * Two mechanisms with no shared failure. Blending them into one green tick would let a failure in
 * either half produce a confident "verified", which is the single worst thing this page could do.
 * They get two cards, side by side, and the page never prints an overall verdict.
 *
 * NOTHING IS UPLOADED ANYWHERE. The file is posted, hashed, inspected and dropped — the server
 * never writes it. Said on the page, because a stranger being asked to hand over a signed contract
 * is entitled to know that before they do it.
 *
 * "NO RECORD" IS NOT "FORGED", and the wording is load-bearing. Documents signed before this
 * register existed, documents signed elsewhere, and field tickets are all legitimately absent.
 * Calling any of them false would turn our own coverage gap into an accusation against a customer's
 * paperwork.
 */
export default function VerifyDocument() {
  // A fingerprint may arrive in the url — that is what the QR on a certificate page encodes, and
  // what somebody with a PAPER copy has instead of a file.
  const { fingerprint: fromUrl } = useParams()
  const brand = useTenantBranding()

  const [typed, setTyped] = useState(fromUrl || '')
  const [result, setResult] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [dragging, setDragging] = useState(false)
  const fileInput = useRef(null)

  const lookUp = useCallback(async (value) => {
    setBusy(true); setError(null); setResult(null)
    try {
      const res = await fetch(`${API_BASE}/verify/${encodeURIComponent(value.trim())}`)
      const body = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(body.detail || 'That does not look like a fingerprint.')
      setResult(body)
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }, [])

  const check = useCallback(async (file) => {
    setBusy(true); setError(null); setResult(null)
    try {
      const form = new FormData()
      form.append('file', file)
      const res = await fetch(`${API_BASE}/verify`, { method: 'POST', body: form })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(body.detail || 'We could not read that file.')
      setResult(body)
      setTyped(body.fingerprint || '')
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }, [])

  // A fingerprint in the url is checked on arrival. Somebody who scanned a QR came here to get an
  // answer, not to press a button.
  useEffect(() => { if (fromUrl) lookUp(fromUrl) }, [fromUrl, lookUp])

  const onDrop = (e) => {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer?.files?.[0]
    if (file) check(file)
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-4">
          <FileSearch className="h-6 w-6 shrink-0 text-slate-500" aria-hidden="true" />
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-slate-900">Check a signed document</h1>
            <p className="truncate text-sm text-slate-500">
              {brand?.company_name || 'Tynvara'}
            </p>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-6 px-4 py-6">
        <section
          onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
            dragging ? 'border-slate-900 bg-white' : 'border-slate-300 bg-white'
          }`}
        >
          <Upload className="mx-auto h-8 w-8 text-slate-400" aria-hidden="true" />
          <p className="mt-3 text-sm font-medium text-slate-900">
            Drop the PDF here, or choose a file
          </p>
          <p className="mt-1 text-sm text-slate-500">
            Your file is not stored. It is read, checked, and discarded.
          </p>
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
            disabled={busy}
          >
            Choose a file
          </button>
          <input
            ref={fileInput}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) check(f) }}
          />
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-5">
          <label htmlFor="fingerprint" className="block text-sm font-medium text-slate-900">
            Working from a printed copy?
          </label>
          <p className="mt-1 text-sm text-slate-500">
            Type the fingerprint printed on the signature certificate. Spaces and line breaks are
            fine — it is printed across two lines so it can be read back.
          </p>
          <form
            className="mt-3 flex flex-col gap-2 sm:flex-row"
            onSubmit={(e) => { e.preventDefault(); if (typed.trim()) lookUp(typed) }}
          >
            <input
              id="fingerprint"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="e.g. 932d2676c1e461ba50d559bba416fbc6 af8da1f74309ae81370c615223d0e349"
              className="min-w-0 flex-1 rounded-md border border-slate-300 px-3 py-2 font-mono text-sm text-slate-900 placeholder:text-slate-400"
            />
            <button
              type="submit"
              disabled={busy || !typed.trim()}
              className="shrink-0 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Look it up
            </button>
          </form>
        </section>

        {busy && <p className="text-sm text-slate-500">Checking…</p>}

        {error && (
          <div className="flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <p>{error}</p>
          </div>
        )}

        {result && <Answers result={result} />}
      </main>
    </div>
  )
}

/**
 * The two answers, side by side and never combined.
 *
 * The layout IS the argument: two cards, each stating what it checked and what it did not. A single
 * headline verdict would hide which half actually answered, and the whole design of this feature is
 * that the two are independent.
 */
function Answers({ result }) {
  const seal = result.seal
  const registry = result.registry || {}

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <SealCard seal={seal} />
        <RegistryCard registry={registry} />
      </div>

      {result.fingerprint && (
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Fingerprint (SHA-256)
          </p>
          {/* Broken the same way the certificate prints it, so the two can be compared character by
              character — which is the only thing anybody ever does with a fingerprint. */}
          <p className="mt-1 break-all font-mono text-sm text-slate-900">
            {result.fingerprint.slice(0, 32)}<br />{result.fingerprint.slice(32)}
          </p>
        </div>
      )}
    </div>
  )
}

function SealCard({ seal }) {
  // NOT SUPPLIED is its own state, and it is the honest answer for a paper copy: a seal lives in
  // the file's bytes, and there are none.
  if (!seal) {
    return (
      <Card tone="neutral" icon={HelpCircle} title="Seal: not checked">
        <p>
          You looked this up by fingerprint, so there was no file to examine. Upload the PDF to have
          its seal checked.
        </p>
      </Card>
    )
  }
  if (seal.sealed === false) {
    return (
      <Card tone="neutral" icon={HelpCircle} title="Not sealed">
        <p>
          This document carries no seal. Documents produced before sealing was introduced do not
          have one, and that says nothing against them — the register below is the check that
          applies.
        </p>
      </Card>
    )
  }
  if (seal.intact === false) {
    return (
      <Card tone="bad" icon={ShieldAlert} title="Altered since it was sealed">
        <p>
          Something in this file has changed since it was signed. Ask the company that issued it for
          a fresh copy, and do not rely on this one.
        </p>
      </Card>
    )
  }
  if (seal.intact === true) {
    return (
      <Card tone="good" icon={ShieldCheck} title="Not altered since it was sealed">
        <p>
          Every byte of this document is as it was when it was sealed{seal.signer ? ` by ${seal.signer}` : ''}.
        </p>
        {/* SAID PLAINLY RATHER THAN OMITTED. The seal is self-signed: it proves the document has not
            moved, and it does not prove who we are to a party who has never heard of us. Letting a
            reader assume otherwise would be the one dishonest thing on this page. */}
        {seal.trusted === false && (
          <p className="mt-2 text-slate-500">
            The seal is issued by the company itself rather than a public certificate authority, so
            a PDF reader may describe it as “validity unknown”. That affects who vouches for the
            identity — not whether the document has been altered, which is what is checked above.
          </p>
        )}
      </Card>
    )
  }
  return (
    <Card tone="neutral" icon={HelpCircle} title="Seal could not be read">
      <p>The seal on this file could not be examined. The register below still applies.</p>
    </Card>
  )
}

function RegistryCard({ registry }) {
  if (!registry.found) {
    return (
      <Card tone="neutral" icon={HelpCircle} title="No record on file">
        <p>{registry.message || 'No signed document on file has this fingerprint.'}</p>
      </Card>
    )
  }
  const original = registry.role === 'signed_original'
  return (
    <Card
      tone="good"
      icon={CheckCircle2}
      title={original ? 'The document that was signed' : 'On file as a signed document'}
    >
      <dl className="space-y-1">
        <Row label="Type" value={registry.kind_label} />
        <Row label="Reference" value={registry.reference} />
        <Row label="Job" value={registry.job} />
        <Row label="Signed for the company by" value={registry.company_signer} />
        <Row label="Signed by the client" value={registry.client_signer} />
        <Row label="Completed" value={fmt(registry.fully_signed_at)} />
      </dl>
      {original && (
        // TOLD APART, not conflated. Somebody holding the pages that were signed WITHOUT the
        // certificate page has a real document, and should know which one it is rather than
        // believe they are holding the complete signed copy.
        <p className="mt-2 text-slate-500">
          This is the document as it was signed. The complete signed copy also carries a signature
          certificate page; ask the company that issued it if you need that version.
        </p>
      )}
    </Card>
  )
}

function Card({ tone, icon: Icon, title, children }) {
  const tones = {
    good: 'border-emerald-300 bg-emerald-50 text-emerald-900',
    bad: 'border-red-300 bg-red-50 text-red-900',
    neutral: 'border-slate-200 bg-white text-slate-700',
  }
  const iconTone = {
    good: 'text-emerald-600', bad: 'text-red-600', neutral: 'text-slate-400',
  }
  return (
    <div className={`rounded-lg border p-5 text-sm ${tones[tone]}`}>
      <div className="flex items-start gap-2">
        <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${iconTone[tone]}`} aria-hidden="true" />
        <div className="min-w-0">
          <h2 className="font-semibold">{title}</h2>
          <div className="mt-1 space-y-1">{children}</div>
        </div>
      </div>
    </div>
  )
}

function Row({ label, value }) {
  if (!value) return null
  return (
    <div className="flex gap-2">
      <dt className="shrink-0 text-slate-500">{label}</dt>
      <dd className="min-w-0 break-words font-medium">{value}</dd>
    </div>
  )
}

const fmt = (v) => {
  if (!v) return ''
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString()
}
